// SEO blog library for Dental Leads Lab. Hardcoded on purpose: fastest to render,
// fully crawlable, no CMS dependency.

export const posts = [
  {
    slug: "how-marketing-grows-a-dental-practice",
    title: "How Marketing Actually Grows a Dental Practice (And What Just Burns Money)",
    metaTitle:
      "How Marketing Grows a Dental Practice: A Practice Owner's Guide | Dental Leads Lab",
    metaDescription:
      "A practical breakdown of how dental marketing grows a practice: the five levers that move production, the numbers that matter, and where most clinics waste budget.",
    excerpt:
      "Most dentists do not have a marketing problem. They have a system problem. Here are the five levers that actually move production, in the order you should fix them.",
    category: "Practice Growth",
    date: "2026-05-12",
    readTime: "9 min read",
    keywords:
      "dental marketing, grow dental practice, dental practice growth, new patient acquisition, dental clinic marketing",
    image:
      "https://images.unsplash.com/photo-1777331903190-341a3dd0441b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
    imageAlt: "Dentist consulting with a patient in a modern dental clinic",
    content: [
      {
        t: "p",
        v: "Ask ten dentists what marketing is and you get ten answers: a logo refresh, a Facebook page, a discount on whitening, a guy who does SEO. Ask them what their cost per new patient was last month and the room goes quiet. That gap is the whole problem. Marketing is not decoration and it is not luck. It is a measurable system that turns local demand into booked, treated, retained patients.",
      },
      {
        t: "p",
        v: "Below is the actual chain of events that grows a practice. Fix them in order. Skipping ahead is why most clinics spend for two years and cannot tell you what it produced.",
      },
      { t: "h2", v: "1. Visibility: be present at the exact moment of intent" },
      {
        t: "p",
        v: "Dentistry is a demand-capture business far more than a demand-creation one. Nobody wakes up wanting a root canal. They get a sharp pain on a Tuesday night, open their phone, and search. Whoever is visible in that ninety-second window gets the case. That is why the first lever is presence at the moment of intent, not brand awareness.",
      },
      {
        t: "ul",
        v: [
          "Google Search ads on high-intent terms: emergency dentist, implants near me, invisalign cost.",
          "A fully built Google Business Profile with real photos, services, and weekly posts.",
          "Local SEO so you appear in the map pack for your suburb, not just your city.",
          "Retargeting for the sixty to eighty percent who look once and leave.",
        ],
      },
      { t: "h2", v: "2. Offer: give the patient a reason to choose you today" },
      {
        p: true,
        t: "p",
        v: "Two clinics on the same street, same ads, same budget. One books three times more. The difference is almost always the offer. Not a discount, an offer: a clear, specific, low-risk first step. A free implant consultation with a 3D scan beats twenty percent off. A same-day emergency slot beats quality dental care since 1998.",
      },
      {
        t: "quote",
        v: "If your ad could be swapped with your competitor's by changing the logo, you do not have an offer. You have a brochure.",
      },
      { t: "h2", v: "3. Conversion: your website is a booking machine, not a portfolio" },
      {
        t: "p",
        v: "Traffic is the expensive part. Conversion is the cheap part, and it is where most practices leave the money. Take a clinic getting 1,000 visits a month at a two percent conversion rate. That is twenty enquiries. Lift conversion to five percent with the same traffic and you have fifty. You just added thirty new patients without spending another rupee or dollar on ads.",
      },
      {
        t: "ul",
        v: [
          "Load in under two seconds on mobile. Every extra second costs roughly seven percent of conversions.",
          "One primary action above the fold: book an appointment. Not six menu items.",
          "Real photos of your team and clinic. Stock smiles read as fake and lower trust.",
          "Proof near the button: review count, star rating, number of cases treated.",
          "Click-to-call on mobile, because over half of dental enquiries are still phone calls.",
        ],
      },
      { t: "h2", v: "4. Speed to lead: the five-minute rule" },
      {
        t: "p",
        v: "This is the single most under-priced growth lever in dentistry. Contact an enquiry within five minutes and you are dramatically more likely to reach and convert them than at thirty minutes. Most practices reply the next morning, by which time the patient has called three other clinics and booked with the one that answered.",
      },
      {
        t: "ol",
        v: [
          "Every form submission triggers an instant notification to a monitored inbox and a phone.",
          "An automatic SMS or WhatsApp goes to the patient within sixty seconds acknowledging them.",
          "A named person owns callbacks, with a target of three attempts in the first 48 hours.",
          "Missed calls get a text-back automatically. A missed call is not a lost patient unless you let it be.",
        ],
      },
      { t: "h2", v: "5. Retention and reactivation: the cheapest growth you own" },
      {
        t: "p",
        v: "Acquiring a new implant patient can cost a meaningful three-figure sum. Reactivating a patient who already trusts you costs the price of a message. Before you raise your ad budget, run your inactive list: patients with unscheduled treatment plans, hygiene recalls overdue by more than nine months, and past enquiries who never booked. Most practices are sitting on six figures of unscheduled production.",
      },
      { t: "h2", v: "The numbers that tell you whether it is working" },
      {
        t: "p",
        v: "If your marketing reports talk about impressions, reach, and engagement, you are reading a vanity report. These are the five metrics that matter, and any partner should give them to you monthly without being asked.",
      },
      {
        t: "ul",
        v: [
          "Cost per booked appointment, not cost per click or cost per lead.",
          "Show-up rate: booked versus actually attended.",
          "Average case value by channel. Search and referral rarely produce the same value.",
          "Return on ad spend measured against treated production, not enquiries.",
          "Lifetime value per new patient, which tells you what you can afford to pay to acquire one.",
        ],
      },
      { t: "h2", v: "Where the money actually gets burned" },
      {
        t: "ul",
        v: [
          "Boosting posts with no offer, no landing page, and no tracking.",
          "Running ads to a homepage instead of a purpose-built page for that treatment.",
          "Paying for broad keywords like dentist that attract price shoppers outside your radius.",
          "No call tracking, so the channel that produces your best cases gets cut at the next budget review.",
          "Changing agencies every four months, which resets every learning phase to zero.",
        ],
      },
      { t: "h2", v: "A realistic ninety-day sequence" },
      {
        t: "ol",
        v: [
          "Days 1 to 14: tracking and measurement. Call tracking, form tracking, conversion events, a single dashboard.",
          "Days 15 to 30: conversion fixes. Treatment landing pages, speed, proof, booking flow, speed-to-lead automation.",
          "Days 31 to 60: demand capture. Search ads on your highest-value treatments, Google Business Profile, review engine.",
          "Days 61 to 90: scale what produced treated cases and kill what produced noise. Add reactivation campaigns.",
        ],
      },
      {
        t: "p",
        v: "None of this is clever. It is just sequenced, measured, and owned by someone accountable. That is what separates a practice that grows predictably from one that hopes the phone rings.",
      },
    ],
  },

  {
    slug: "why-paid-ads-work-for-new-patient-acquisition",
    title: "Why Paid Ads Are Still the Fastest Way to Acquire New Dental Patients",
    metaTitle:
      "Why Paid Ads Work for Dental New Patient Acquisition | Dental Leads Lab",
    metaDescription:
      "Google Ads put your clinic in front of patients at the exact moment they are searching. Here is how paid search works for dentists, what it costs, and how to measure real ROI.",
    excerpt:
      "SEO compounds. Referrals are free. But when a chair is empty next week, paid search is the only channel you can turn on today and measure by Friday.",
    category: "Paid Advertising",
    date: "2026-05-21",
    readTime: "8 min read",
    keywords:
      "dental google ads, paid ads for dentists, dental PPC, new patient acquisition, dental advertising ROI",
    image:
      "https://images.unsplash.com/photo-1667133295311-e6911e6e22db?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
    imageAlt: "Dental professional reviewing a digital scan on a screen",
    content: [
      {
        t: "p",
        v: "There is a fair criticism of paid advertising: the moment you stop paying, it stops working. True. But there is a reason nearly every high-growth practice runs it anyway. Paid search is the only channel where you can be in front of a patient with a specific, urgent, high-value need within hours of deciding to be, and know by the end of the week what it produced.",
      },
      { t: "h2", v: "Search ads are not interruption, they are intent" },
      {
        t: "p",
        v: "Social ads interrupt someone scrolling. Search ads answer a question someone just typed. When a person searches emergency dentist open now or dental implants cost near me, they are not browsing. They are shortlisting. The average value of a single implant or full-arch case makes that click worth more in dentistry than in almost any other local service category.",
      },
      {
        t: "quote",
        v: "You are not buying clicks. You are buying position in a ninety-second decision window.",
      },
      { t: "h2", v: "Speed: the only channel that works next week" },
      {
        t: "p",
        v: "Local SEO is the best long-term investment a practice can make, and it typically takes sixty to a hundred and twenty days to move meaningfully. Referrals take years to build. Paid search can be live in 48 hours. That timing difference matters enormously in three situations: a new clinic opening, a new operatory or dentist added, and a specific treatment you want to grow now.",
      },
      { t: "h2", v: "What dental paid search actually costs" },
      {
        t: "p",
        v: "Costs vary sharply by city and treatment, so treat these as structural rather than absolute. Emergency and general terms are cheaper per click but convert fast. Implant and orthodontic terms are expensive per click and slower to convert, but a single case can pay for a full month of spend.",
      },
      {
        t: "ul",
        v: [
          "General and emergency dentistry: lowest cost per click, highest volume, fastest bookings.",
          "Cosmetic and orthodontics: mid to high cost per click, longer consideration, strong case value.",
          "Implants and full-arch: highest cost per click and highest case value. One case often returns several months of spend.",
          "Branded terms: cheapest of all, and worth defending so a competitor does not buy your name.",
        ],
      },
      {
        t: "p",
        v: "The metric that matters is not cost per click. It is cost per treated case against average case value. A ninety-dollar cost per enquiry looks expensive next to a social campaign at nine dollars, right up until you notice the ninety-dollar enquiries book implants and the nine-dollar ones wanted a free whitening voucher.",
      },
      { t: "h2", v: "Why most dental ad accounts fail" },
      {
        t: "ol",
        v: [
          "Ads point at the homepage. The patient searched for implants and landed on a page about your practice history. They leave.",
          "Broad match keywords with no negatives, so budget goes to dental assistant jobs and dentist salary.",
          "No location or radius discipline, drawing enquiries from people who will never drive to you.",
          "Conversion tracking not installed, so optimisation is guesswork and Google optimises for the wrong outcome.",
          "No call tracking, despite phone calls being the majority of dental enquiries.",
          "Nobody answers the phone within five minutes, so paid enquiries quietly die in a voicemail box.",
        ],
      },
      { t: "h2", v: "The structure that works" },
      {
        t: "ul",
        v: [
          "One campaign per treatment category, so budget and bids follow case value.",
          "A dedicated landing page per treatment, with one action, proof, pricing transparency, and click-to-call.",
          "Tight keyword sets with an aggressive negative list, reviewed weekly for the first month.",
          "Call tracking numbers plus form tracking, both fed back into the ad platform as conversions.",
          "Offline conversion import where possible, so the platform learns which enquiries became treated cases.",
          "Speed-to-lead automation: instant internal notification, instant patient acknowledgement.",
        ],
      },
      { t: "h2", v: "Paid ads and SEO are not rivals" },
      {
        t: "p",
        v: "The honest position is that paid search buys you time while organic compounds. Run ads to fill chairs and generate data now. Use what the ad data tells you, which keywords convert, which offers land, which treatments have margin, to direct your SEO and content investment. Practices that run both share intelligence between them and almost always outperform practices that treat them as separate budgets.",
      },
      { t: "h2", v: "How to judge a paid campaign fairly" },
      {
        t: "p",
        v: "Give a properly structured account sixty days before making a verdict, then judge it on three questions. Did cost per booked appointment fall month over month? Did average case value from paid hold up? Did treated production exceed total marketing investment by a multiple you are happy with? If the answer to all three is yes, scale. If the reporting cannot answer them, that is not an advertising problem. That is a measurement problem, and it is fixable in two weeks.",
      },
    ],
  },

  {
    slug: "dental-marketing-mistakes-clinics-make",
    title: "9 Marketing Mistakes Dental Clinics Make (And What Each One Costs)",
    metaTitle: "9 Common Dental Marketing Mistakes and How to Fix Them | Dental Leads Lab",
    metaDescription:
      "The nine marketing mistakes we see most often in dental practices, what each one quietly costs in lost new patients, and the fix for every single one.",
    excerpt:
      "We have audited hundreds of dental practices. The same nine mistakes appear again and again, and every one of them is cheaper to fix than to keep paying for.",
    category: "Audit",
    date: "2026-06-02",
    readTime: "10 min read",
    keywords:
      "dental marketing mistakes, dental clinic marketing, dental practice advertising errors, dental lead generation",
    image:
      "https://images.unsplash.com/photo-1762625570087-6d98fca29531?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
    imageAlt: "Clean modern clinic waiting area with seating",
    content: [
      {
        t: "p",
        v: "Marketing rarely fails dramatically. It fails quietly, through a handful of small, fixable leaks that never show up on a report because nobody is measuring the right thing. Here are the nine we find most often, roughly in order of how much money they cost.",
      },
      { t: "h2", v: "1. Nobody answers fast enough" },
      {
        t: "p",
        v: "You can have perfect ads, a beautiful site, and a strong offer, and still lose most of your enquiries at the front desk. Enquiries contacted within five minutes convert far better than those contacted hours later. If your average response time is next business morning, you are funding your competitors' calendars.",
      },
      {
        t: "p",
        v: "Fix: instant notification on every submission, an automatic acknowledgement to the patient within a minute, one named owner for callbacks, and automatic text-back on missed calls.",
      },
      { t: "h2", v: "2. Ads point at the homepage" },
      {
        t: "p",
        v: "Someone searches for clear aligners, clicks your ad, and lands on a general homepage with a carousel and a practice mission statement. They have to hunt for what they came for, and they will not. Every treatment you advertise needs its own page that matches the search word for word.",
      },
      { t: "h2", v: "3. No call tracking" },
      {
        t: "p",
        v: "In dentistry the majority of high-value enquiries are phone calls. If calls are not tracked back to a source, your reporting is fiction. The predictable consequence: at the next budget review, you cut the channel that was actually producing your implant cases because it showed zero leads.",
      },
      { t: "h2", v: "4. Chasing volume instead of case value" },
      {
        t: "p",
        v: "Free consultation and heavy-discount campaigns produce impressive enquiry counts and disappointing production. Twenty enquiries at a low cost each is worse than four enquiries that become two implant cases. Judge every channel on treated production, never on enquiry count.",
      },
      { t: "h2", v: "5. A website that is slow on mobile" },
      {
        t: "p",
        v: "Most dental traffic is mobile, often on patchy connections. Oversized hero images, five tracking scripts, and an embedded chat widget can push load time past four seconds. Every additional second measurably reduces conversions. This is usually a one-week fix with a large payoff.",
      },
      {
        t: "ul",
        v: [
          "Compress and correctly size all images.",
          "Remove scripts nobody reads the data from.",
          "Keep the primary booking action above the fold.",
          "Test on a real mid-range phone, not just a desktop browser.",
        ],
      },
      { t: "h2", v: "6. Ignoring the Google Business Profile" },
      {
        t: "p",
        v: "For a local practice, the map pack often outperforms the website for enquiry volume, and it is free. Yet most profiles have stale hours, four photos from 2019, no services listed, unanswered reviews, and no posts. Treat the profile as a second website. Photos weekly, every review answered, services and treatments fully listed, Q&A seeded with real patient questions.",
      },
      { t: "h2", v: "7. Reviews left to chance" },
      {
        t: "p",
        v: "Star rating and review count directly influence who gets clicked in the map pack. Practices that ask systematically, at the right moment, from the right patients, pull ahead within a quarter. Practices that hope for reviews accumulate a handful a year, mostly from unhappy people who are the most motivated to write.",
      },
      { t: "h2", v: "8. Changing agency or channel every few months" },
      {
        t: "p",
        v: "Paid campaigns need weeks of data before optimisation becomes meaningful. SEO needs quarters. Practices that switch partners every four months restart the learning curve each time and pay onboarding costs repeatedly while never reaching the point where results compound. Set clear KPIs, agree a ninety-day window, then judge honestly.",
      },
      { t: "h2", v: "9. The inactive patient list nobody touches" },
      {
        t: "p",
        v: "Unscheduled treatment plans, overdue hygiene recalls, and old enquiries who never booked. This is the highest-return marketing available to any practice and it costs almost nothing, because these people already trust you. Most clinics we audit are sitting on a substantial amount of unscheduled production in their own database while raising their ad budget.",
      },
      { t: "h2", v: "How to check yourself in thirty minutes" },
      {
        t: "ol",
        v: [
          "Submit your own website form. Time how long until a human contacts you.",
          "Call your main line at 6pm. See what happens.",
          "Open your site on a phone on mobile data and count the seconds to first paint.",
          "Search your main treatment plus your suburb. Note where you appear and who is above you.",
          "Ask for last month's cost per booked appointment. If nobody can answer, start there.",
        ],
      },
      {
        t: "p",
        v: "Every item on this list is a systems problem, not a talent problem. Which is good news, because systems are fixable in weeks.",
      },
    ],
  },

  {
    slug: "local-seo-for-dentists-guide",
    title: "Local SEO for Dentists: The Complete Guide to Owning Your Suburb",
    metaTitle: "Local SEO for Dentists: Complete Guide to Ranking in the Map Pack | Dental Leads Lab",
    metaDescription:
      "A step-by-step local SEO guide for dental practices: Google Business Profile optimisation, reviews, location pages, citations, and the on-page basics that drive map pack rankings.",
    excerpt:
      "Local SEO is the highest-margin channel in dentistry. It takes a quarter to build and it keeps paying long after the work stops. Here is the full playbook.",
    category: "SEO",
    date: "2026-06-09",
    readTime: "11 min read",
    keywords:
      "local seo for dentists, dental seo, google business profile dentist, dental map pack ranking, dentist near me",
    image:
      "https://images.unsplash.com/photo-1616391182219-e080b4d1043a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
    imageAlt: "Modern dental operatory with treatment chairs",
    content: [
      {
        t: "p",
        v: "When someone searches dentist near me, three results sit in a map box above everything else. Those three get the overwhelming majority of the clicks. Local SEO is the work of becoming one of them. Unlike ads, it does not switch off when the invoice stops, which is exactly why it deserves a permanent line in your budget.",
      },
      { t: "h2", v: "What Google is actually deciding" },
      {
        t: "p",
        v: "Local ranking comes down to three things: relevance, how well your profile matches the search; distance, how close you are to the searcher; and prominence, how established and trusted you look across the web. You cannot move your clinic, so the work concentrates on relevance and prominence.",
      },
      { t: "h2", v: "Step 1: Treat your Google Business Profile as a second website" },
      {
        t: "ul",
        v: [
          "Exact, consistent name, address and phone number. No keyword stuffing in the business name.",
          "Primary category Dentist, with accurate secondary categories such as Dental Implants Periodontist or Orthodontist.",
          "Every service listed individually with a written description, not just a bare list.",
          "Twenty or more real photos: exterior, reception, operatories, team, equipment. Add new ones weekly.",
          "Accurate hours including holidays, plus attributes like wheelchair accessible and evening appointments.",
          "Weekly posts. Treatment explainers, team news, offers. Signals an active, real business.",
          "Q&A seeded with the questions your front desk answers every day.",
        ],
      },
      { t: "h2", v: "Step 2: Build a review engine, not a review hope" },
      {
        t: "p",
        v: "Review volume, recency, rating, and your response rate all influence prominence. The practices that win do not ask better, they ask systematically.",
      },
      {
        t: "ol",
        v: [
          "Ask at the moment of maximum relief: immediately after a successful treatment, in person.",
          "Follow with an SMS containing a direct review link. Never a printed instruction sheet.",
          "Respond to every review within 48 hours, positive and negative, in your own voice.",
          "Aim for a steady flow every month rather than a burst of twenty then silence.",
        ],
      },
      {
        t: "quote",
        v: "A four point seven rating with reviews from this month beats a five point zero from three years ago. Recency is a ranking signal and a trust signal.",
      },
      { t: "h2", v: "Step 3: On-page basics most dental sites get wrong" },
      {
        t: "ul",
        v: [
          "One clear H1 per page containing the treatment and the location.",
          "A unique title tag and meta description on every page. No duplicates, no empty descriptions.",
          "A dedicated page per treatment: implants, aligners, root canal, whitening, emergency care.",
          "A dedicated page per location if you have multiple clinics, with genuinely different content.",
          "LocalBusiness or Dentist schema markup with address, phone, opening hours and geo coordinates.",
          "Internal links from treatment pages to the booking page using descriptive anchor text.",
          "Fast mobile load times. Core Web Vitals are part of page experience.",
        ],
      },
      { t: "h2", v: "Step 4: Citations and consistency" },
      {
        t: "p",
        v: "Your name, address and phone number should be identical everywhere it appears: directories, dental associations, insurance listings, health portals, your own site footer. Inconsistency creates doubt about which record is real and suppresses prominence. Fix your top twenty citations and you have captured most of the available benefit.",
      },
      { t: "h2", v: "Step 5: Content that answers real patient questions" },
      {
        t: "p",
        v: "Patients search in plain language before they search in clinical language. How much does a dental implant cost. Is a root canal painful. How long do braces take. Each of those is a page you can own, and each one pulls in someone at the research stage who will need a clinic in a few weeks.",
      },
      {
        t: "ul",
          v: [
          "Cost and pricing explainers, with ranges and the factors that change them.",
          "Pain, recovery and aftercare guides for your highest-value treatments.",
          "Comparison pages: implants versus bridges, aligners versus fixed braces.",
          "Before and after case notes with real permissioned photography.",
        ],
      },
      { t: "h2", v: "Step 6: What to measure monthly" },
      {
        t: "ul",
        v: [
          "Map pack rank for your top five treatment plus suburb combinations.",
          "Google Business Profile calls, direction requests and website clicks.",
          "Organic enquiries and, more importantly, organic booked appointments.",
          "New review volume and average rating trend.",
          "Core Web Vitals status in Search Console.",
        ],
      },
      { t: "h2", v: "A realistic timeline" },
      {
        t: "p",
        v: "Weeks one to three: profile overhaul, schema, titles and descriptions, citation cleanup. Weeks four to eight: treatment pages, review engine live, speed work. Weeks nine to sixteen: content cadence and link earning. Expect early profile movement inside a month and meaningful organic ranking movement between sixty and a hundred and twenty days. Anyone promising page one in two weeks is either buying ads or making it up.",
      },
    ],
  },

  {
    slug: "what-should-a-new-dental-patient-cost",
    title: "What Should a New Dental Patient Cost You? A Practical Guide to CAC and LTV",
    metaTitle: "New Patient Acquisition Cost for Dentists: CAC and LTV Explained | Dental Leads Lab",
    metaDescription:
      "How to calculate your true cost per new dental patient, work out lifetime value by treatment, and decide what you can profitably afford to spend on marketing.",
    excerpt:
      "Most dentists have no idea what a new patient costs them, which means they cannot tell a good marketing month from a bad one. Here is how to fix that in an afternoon.",
    category: "Numbers",
    date: "2026-06-16",
    readTime: "8 min read",
    keywords:
      "dental patient acquisition cost, dental CAC, dental patient lifetime value, dental marketing ROI, cost per new patient dentist",
    image:
      "https://images.unsplash.com/photo-1609207825181-52d3214556dd?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
    imageAlt: "Practice owner reviewing performance data on a monitor",
    content: [
      {
        t: "p",
        v: "Here is a question worth asking at your next practice meeting: what did our last new patient cost us, and what will they be worth? If nobody can answer within a minute, every marketing decision you make is being made on feel. These two numbers, customer acquisition cost and lifetime value, turn marketing from an expense into arithmetic.",
      },
      { t: "h2", v: "Calculating your real acquisition cost" },
      {
        t: "p",
        v: "Total marketing investment for the period, divided by the number of new patients who actually attended. Not enquiries. Not bookings. Attended.",
      },
      {
        t: "ul",
        v: [
          "Include ad spend, agency or in-house cost, software, and content or photography production.",
          "Count only genuinely new patients who sat in a chair, not returning patients.",
          "Run it monthly and by channel. A blended number hides your best and worst performers.",
        ],
      },
      {
        t: "p",
        v: "Two practices can report the same blended cost per patient while one is highly profitable and one is losing money, because the mix behind the average is completely different. Channel-level reporting is where the decisions live.",
      },
      { t: "h2", v: "Calculating lifetime value without a finance degree" },
      {
        t: "p",
        v: "Start simple: average first-year revenue per new patient. Then layer in retention and referral. A hygiene patient who stays five years and brings two family members is worth several multiples of their first visit. An implant case may deliver most of its value in ninety days.",
      },
      {
        t: "ol",
        v: [
          "Average revenue from a new patient's first twelve months.",
          "Multiply by average years retained, using your own recall data.",
          "Add referred revenue attributable to that patient if you track referral source.",
          "Apply your treatment margin to get contribution, not just top-line revenue.",
        ],
      },
      { t: "h2", v: "The ratio that tells you what to do next" },
      {
        t: "p",
        v: "Divide lifetime value by acquisition cost. If the ratio is comfortably above three, you should almost certainly be spending more, not less. Between two and three, there is room but the funnel needs tightening first. Below two, do not touch the ad budget: fix conversion, speed to lead, and case acceptance, because more traffic will only amplify a leak.",
      },
      {
        t: "quote",
        v: "A practice that knows its numbers can outbid a practice that does not, because it knows exactly what a patient is worth and the other is guessing.",
      },
      { t: "h2", v: "Why cost per patient looks worse than it is" },
      {
        t: "p",
        v: "Three accounting habits make marketing look more expensive than it truly is. Attributing only first-visit revenue and ignoring the treatment plan that follows. Ignoring referrals generated by acquired patients. And crediting phone enquiries to nowhere because calls are untracked, which inflates the apparent cost of every channel that drives calls.",
      },
      { t: "h2", v: "The levers that lower acquisition cost" },
      {
        t: "ul",
        v: [
          "Conversion rate on your booking page. The cheapest lever, and usually the loosest.",
          "Speed to lead. Faster contact means a higher share of enquiries become attended visits.",
          "Show-up rate. Confirmation sequences and reminders often recover ten percent or more.",
          "Keyword and audience tightening, so you stop paying for people outside your radius.",
          "Reactivation campaigns, which produce production at a fraction of new-patient cost.",
        ],
      },
      { t: "h2", v: "A one-page dashboard worth building" },
      {
        t: "p",
        v: "Enquiries by channel. Booked appointments by channel. Attended by channel. Cost per attended patient by channel. Average case value by channel. Treated production against total marketing investment. Six rows, updated monthly. It takes an afternoon to build and it will change how you spend for years.",
      },
    ],
  },

  {
    slug: "dental-website-that-books-appointments",
    title: "The 7 Elements of a Dental Website That Actually Books Appointments",
    metaTitle: "7 Elements of a High-Converting Dental Website | Dental Leads Lab",
    metaDescription:
      "Your dental website has one job: turn visitors into booked appointments. Here are the seven elements that decide whether it does, and how to audit each one today.",
    excerpt:
      "A beautiful dental website that does not book appointments is an expensive brochure. These seven elements are what separate the two.",
    category: "Conversion",
    date: "2026-06-23",
    readTime: "9 min read",
    keywords:
      "dental website design, high converting dental website, dental website conversion, dentist website booking",
    image:
      "https://images.unsplash.com/photo-1588776814546-daab30f310ce?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
    imageAlt: "Dental team performing a procedure in a clinical setting",
    content: [
      {
        t: "p",
        v: "Traffic is the expensive part of marketing. Conversion is the cheap part. Doubling your booking rate has exactly the same effect as doubling your ad budget, and it costs a fraction as much. Here is where that lift actually comes from.",
      },
      { t: "h2", v: "1. One obvious action, repeated" },
      {
        t: "p",
        v: "A visitor should never have to work out what to do next. One primary action, book an appointment, visible above the fold and repeated after every major section. Secondary actions like call us or download a guide are fine, but they must look secondary. When everything is emphasised, nothing is.",
      },
      { t: "h2", v: "2. A headline about the patient, not the practice" },
      {
        t: "p",
        v: "Comprehensive dental care since 1998 says nothing a patient cares about. Same-day emergency appointments in Bandra, or implants with a fixed quote before you commit, speaks directly to the reason they searched. Match the headline to the intent of the traffic hitting that page.",
      },
      { t: "h2", v: "3. Proof placed next to the decision" },
      {
        t: "ul",
        v: [
          "Star rating and review count, shown near the booking button.",
          "Two or three short patient quotes naming the specific treatment.",
          "Real before and after photography with permission, not stock imagery.",
          "Credentials, memberships and years in practice, stated plainly.",
        ],
      },
      { t: "h2", v: "4. Real photography of your real clinic" },
      {
        t: "p",
        v: "Patients are deciding whether to let a stranger work inside their mouth. Generic stock photos of models with perfect teeth reduce trust because visitors have seen them on ten other sites. A single afternoon with a photographer covering reception, operatories, equipment and your team outperforms any design refresh.",
      },
      { t: "h2", v: "5. Pricing transparency, even if it is a range" },
      {
        t: "p",
        v: "Cost is the first question and the biggest source of drop-off. Refusing to discuss it does not remove the question, it just sends the visitor to a competitor who answered. Publish ranges, explain what changes the number, and offer a fixed quote after assessment. You will lose a few price shoppers and win far more serious cases.",
      },
      { t: "h2", v: "6. A booking form that respects their time" },
      {
        t: "ol",
        v: [
          "Ask for the minimum: name, phone, email, treatment interest. Nothing else.",
          "Offer a date and time preference so the patient feels in control.",
          "Confirm instantly on screen and by message, with a clear statement of what happens next.",
          "Keep click-to-call prominent on mobile. Many patients will always prefer to talk.",
        ],
      },
      { t: "h2", v: "7. Speed, on a real phone, on real data" },
      {
        t: "p",
        v: "Test your site on a mid-range phone on mobile data, not on office wifi on a desktop. Aim for meaningful content in under two seconds. The usual offenders are uncompressed hero images, unused tracking scripts, heavy chat widgets, and multiple font families loaded in full.",
      },
      {
        t: "quote",
        v: "If your homepage takes four seconds on a phone, you are paying full price for traffic and receiving a discount version of it.",
      },
      { t: "h2", v: "A ten-minute self audit" },
      {
        t: "ol",
        v: [
          "Open your homepage on a phone. Can you book in one tap without scrolling?",
          "Read your headline aloud. Does it describe a patient problem or your practice history?",
          "Count how many actions compete above the fold. More than two is too many.",
          "Find your review rating. If you had to hunt, so does everyone else.",
          "Submit the form yourself and time the response. This is the leak that costs most.",
        ],
      },
      {
        t: "p",
        v: "None of these seven require a rebuild. Most are a week of focused work on the site you already own, and they compound with every rupee and dollar you spend on traffic afterwards.",
      },
    ],
  },
];

export const getPost = (slug) => posts.find((p) => p.slug === slug);

export const sortedPosts = [...posts].sort((a, b) => (a.date < b.date ? 1 : -1));
